import {
  Debouncer,
  ffLazyLoading,
  hasFeatureFlag,
  logger,
  ObjUtil,
  PromiseUtil,
  Time,
  Util
} from 'yougile-util'
import {
  DataObjectId,
  DataObjectJSON,
  Hub,
  HubUtil,
  LazyLoadGetRequest,
  LazyLoadGetResult,
  SandboxTree
} from 'yougile-model'
import {Backend} from '@common/backend'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {getLazyMessagesBlock} from './lazy-load'
import {getSafeObjFromLocalStorage} from './storage'
import {Sync} from './sync'
import {SyncEvents} from './sync-events'

interface HubRequest {
  promise: Promise<void>
  resolve: () => void
  reject: (error: Error) => void
  inProgress: boolean
  isSuccessful?: boolean
}

const usageStorageKey = 'hub-last-usage'
const usageThreshold = 1000 * 60 * 60 * 24
const updateDebouncer = new Debouncer(500)

const reportIfNeeded = (): void => {
  if (!hasFeatureFlag(ffLazyLoading)) {
    return
  }

  try {
    if (LazyLoadClient.requestsCount > 5_000) {
      const lastReportTime = localStorage.getItem('lazy-load-report-time')
      if (!lastReportTime || Date.now() - +lastReportTime > Time.hour) {
        const reasonsCounts: {[key: string]: number} = {}
        for (const id in LazyLoadClient.loadReasons) {
          const reasons = LazyLoadClient.loadReasons[id as DataObjectId]
          for (const reason of reasons) {
            reasonsCounts[reason] = (reasonsCounts[reason] ?? 0) + 1
          }
        }
        Backend.report(
          'lazy-load-too-many-requests',
          JSON.stringify(
            {
              requestsCount: LazyLoadClient.requestsCount,
              reasonsCounts
            },
            null,
            2
          )
        )
        localStorage.setItem('lazy-load-report-time', Date.now().toString())
      }
    }
  } catch (error) {
    logger.error(error)
  }
}

export class LazyLoadClient {
  // companyId -> id -> request
  private static requests = new Map<
    DataObjectId,
    Map<DataObjectId, HubRequest>
  >()
  private static initialized = false

  private static errorsCount = 0

  private static needToSaveUsage = false
  private static lastUsage: Map<DataObjectId, number> = new Map()

  public static loadReasons: {[key: DataObjectId]: string[]} = {}

  public static get requestsCount(): number {
    const companies = this.requests.values()
    let count = 0
    for (const company of companies) {
      count += company.size
    }
    return count
  }

  static async getMessages(
    hub: Hub,
    messageTimestamp: number,
    before?: number,
    after?: number
  ): Promise<void> {
    const hubJSON = hub.toJSON()
    let wasChanged = false
    if (hubJSON.chat) {
      const box = Sync.get(hub)
      if (!hubJSON.chat.messages[messageTimestamp] || before || !after) {
        const {messages, totalMessages} = await getLazyMessagesBlock({
          hubId: hub.id,
          messageTimestamp,
          before,
          after,
          companyId: hub.companyId
        })

        for (const message of messages) {
          wasChanged = true
          hubJSON.chat.messages[message.timestamp] = message
        }

        hubJSON.chat.messagesCount = totalMessages
      }

      const placeholders = [
        hubJSON.chat?.messages[messageTimestamp - 1],
        hubJSON.chat?.messages[messageTimestamp + 1]
      ].filter(m => m?.properties?.isLazyPlaceholder)
      if (placeholders.length) {
        wasChanged = true
        for (const placeholder of placeholders) {
          delete hubJSON.chat.messages[placeholder.timestamp]
        }
      }

      if (wasChanged) {
        Sync.applyPatch(
          hub.companyId,
          box.revision,
          {
            [hub.id]: hubJSON
          },
          'exact'
        )
      }
    }
  }

  static async waitLoad(
    box: SandboxTree,
    id: DataObjectId,
    reason: string
  ): Promise<void> {
    this.loadReasons[id] = [...(this.loadReasons[id] ?? []), reason ?? '']
    this.updateUsage(id)
    const current = box.getHub(id)
    const alreadyLoadedHub = current && !current.isLazy
    if (alreadyLoadedHub) {
      return
    }
    const companyRequests: Map<DataObjectId, HubRequest> =
      this.requests.get(box.companyId) ?? new Map()
    this.requests.set(box.companyId, companyRequests)

    const request = companyRequests.get(id)
    if (request) {
      return request.promise
    }
    const [newPromise, resolve, reject] = PromiseUtil.withResolvers<void>()
    const req: Omit<HubRequest, 'promise'> = {
      resolve,
      reject,
      inProgress: false
    }

    companyRequests.set(id, {...req, promise: newPromise})
    reportIfNeeded()
    return newPromise
  }

  private static loadUsage(): void {
    const storedUsage: {[key: DataObjectId]: number} =
      getSafeObjFromLocalStorage(usageStorageKey)
    if (storedUsage) {
      this.lastUsage = new Map(
        Object.entries(storedUsage).map(([k, v]: [DataObjectId, number]) => [
          k,
          v
        ])
      )
    }
  }

  private static keepLazyChatsRoutine(): void {
    const box = Sync.getCurrentSandbox()
    const company = box.getHub(box.companyId)
    const updates: {[key: DataObjectId]: DataObjectJSON} = {}
    const openedChat = ChatUtils.getOpenedChatHub()
    box.walkForward(company, hub => {
      if (hub.chat.computeIsLazyChat() && hub.id !== openedChat?.id) {
        const hubJSON = hub.toJSON()
        const newHubJSON = HubUtil.trimChat(hubJSON)
        updates[hub.id] = newHubJSON
      }
      return true
    })
    if (ObjUtil.keys(updates).length) {
      Sync.applyPatch(box.companyId, box.revision, updates, 'exact')
    }
  }

  static init(): void {
    if (!hasFeatureFlag(ffLazyLoading)) {
      return
    }
    if (this.initialized) {
      return
    }
    this.initialized = true
    PromiseUtil.startRoutine(this.routine.bind(this), 1, 's')
    PromiseUtil.startRoutine(this.clearOldHubsRoutine.bind(this), 1, 'h')
    PromiseUtil.startRoutine(this.keepLazyChatsRoutine.bind(this), 1, 'h')
    this.loadUsage()

    window.addEventListener('beforeunload', () => {
      this.saveUsage()
    })

    window.addEventListener('online', () => {
      this.errorsCount = 0
    })
  }

  private static saveUsage(): void {
    localStorage.setItem(
      usageStorageKey,
      JSON.stringify(Object.fromEntries(this.lastUsage))
    )
  }

  private static updateUsage(id: DataObjectId): void {
    this.lastUsage.set(id, Date.now())
    this.needToSaveUsage = true
  }

  private static async clearOldHubsRoutine(): Promise<void> {
    const now = Date.now()
    for (const hubId of this.lastUsage.keys()) {
      const lastUsage = this.lastUsage.get(hubId)
      if (typeof lastUsage !== 'number') {
        continue
      }
      const isExpired = now - lastUsage > usageThreshold
      if (isExpired) {
        const box = Sync.getCurrentSandbox()
        const hub = box?.getHub(hubId)
        if (hub) {
          hub.unload()
          this.lastUsage.delete(hubId)
        }
      }
    }
  }

  private static async routine(): Promise<void> {
    const data: LazyLoadGetRequest = {}
    const usedRequests: HubRequest[] = []
    for (const [companyId, requests] of this.requests) {
      data[companyId] = {
        ids: [...requests.keys()]
          .map(id => ({...requests.get(id), id}))
          .filter(r => r && !r.inProgress)
          .map(r => r.id)
      }
      for (const id of data[companyId].ids) {
        const req = requests.get(id)
        if (req) {
          req.inProgress = true
          usedRequests.push(req)
        }
      }
      if (data[companyId].ids.length === 0) {
        delete data[companyId]
      }
    }
    if (ObjUtil.isEmpty(data)) {
      return
    }

    const res = await Backend.postJSON<LazyLoadGetResult>(
      '/data/lazy-load/get',
      {...Backend.loadCred(), data}
    )
    const isOk = res.result === 'ok' && res.data
    if (isOk) {
      this.applyResult(res.data)
      this.errorsCount = 0
      usedRequests.forEach(req => {
        req.resolve()
        req.isSuccessful = true
      })
    } else {
      this.errorsCount++
      await Util.wait(1000 * 2 ** this.errorsCount)
    }

    usedRequests.forEach(req => (req.inProgress = false))

    for (const [companyId, requests] of this.requests) {
      for (const id of requests.keys()) {
        const req = requests.get(id)
        if (req?.isSuccessful) {
          requests.delete(id)
        }
      }
      if (requests.size === 0) {
        this.requests.delete(companyId)
      }
    }

    if (this.needToSaveUsage) {
      this.saveUsage()
      this.needToSaveUsage = false
    }
  }

  private static applyResult(data: LazyLoadGetResult['data']): void {
    const patches = new Map<
      DataObjectId,
      {[id: DataObjectId]: DataObjectJSON}
    >()
    for (const companyId of ObjUtil.keys(data)) {
      patches.set(companyId, data[companyId].objects ?? {})
      const patch = data[companyId].objects
      const box = Sync.get({companyId})
      if (box && patch) {
        Sync.applyPatch(companyId, box.revision, patch, 'exact')
      }
    }
    updateDebouncer.runLatest(() => {
      SyncEvents.triggerAll(globals.getCompanyId())
    })
  }
}
